import { maxBy, sortBy, sumBy, uniq } from "lodash";
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    Link as MaterialLink,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { RelativeTimeText, TimestampText } from "../../widgets/date_time";
import React from "react";
import {
    AccessLevel,
    CardValidity,
    CustomerPageQuery,
    CustomerSavedCardFragment,
    InvoiceFragment,
    NetsOperation,
    NetsProcessResponseFragment,
    NetsTerminal,
    NetsTransactionFragment,
    NetsTransactionPreAuthFragment,
    PaymentReconciliationFragment,
} from "../../generated/gql/graphql";
import { formatMoney } from "../NetsTransactionsListPage";
import { JsonDiagnosticButton } from "../../widgets/diagnostics";
import { TraceButton } from "../../widgets/TraceButton";
import { hasAccessLevel, isSuperUser } from "../../util/active_user_context";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Link } from "react-router-dom";
import { formatTicketId } from "../../util/formatting";
import { NetsResponseCode } from "../../widgets/NetsResponseCode";
import WarningIcon from "@mui/icons-material/Warning";
import { ISODate } from "../statistics/StatisticsProcessingPage";
import { DateTimeFormat, formatDateMed, parseTime } from "../../util/date_time";
import { Property, PropertyList } from "../ticket_v2/TicketPageHeader";
import { mergeTransactions, TransactionRef } from "./TransactionUnion";
import { AccessTime, PlayArrow } from "@mui/icons-material";
import { NetsXmlButton } from "../../widgets/NetsXmlButton";
import { TransactionPreAuthButton } from "../../widgets/TransactionPreAuthButton";

const timestampColumnWidth = "180px";
const amountColumnWidth = "100px";

function getCardValidityChip(validity: CardValidity) {
    switch (validity) {
        case CardValidity.Active:
            return { label: "Aktiv", color: "success" as const };
        case CardValidity.Expired:
            return { label: "Utløpt", color: "warning" as const };
        case CardValidity.Removed:
            return { label: "Fjernet av kunde", color: "default" as const };
        case CardValidity.Replaced:
            return { label: "Kort byttet ut", color: "default" as const };
        case CardValidity.TerminalFailed:
            return { label: "Kortregistrering feilet", color: "error" as const };
        case CardValidity.TerminalPending:
            return { label: "Kortregistrering startet", color: "warning" as const };
        default:
            return { label: "Ukjent", color: "default" as const };
    }
}

function formatExpirationDate(expirationDate: string | null | undefined): string | null {
    if (!expirationDate) return null;
    // Format from "2033-01-31" to "01/33"
    const parts = expirationDate.split("-");
    if (parts.length !== 3) return expirationDate;
    const year = parts[0].slice(2); // Get last 2 digits of year
    const month = parts[1];
    return `${month}/${year}`;
}

export function CustomerSavedCardsTab({ customer }: { customer: CustomerPageQuery["customer"] }) {
    const savedCards = sortBy(customer.savedCards as unknown as ReadonlyArray<CustomerSavedCardFragment>, "id");
    savedCards.reverse();

    return (
        <div>
            {hasAccessLevel(AccessLevel.Project) ? (
                <Button
                    href={"/api/admin/nets/reconcile?customerId=" + customer.id}
                    target="_blank"
                    startIcon={<PlayArrow />}
                >
                    Kjør betalingsjobb
                </Button>
            ) : null}

            <SavedCardsTable savedCards={savedCards} />

            <PaymentReconciliationsTable
                paymentReconciliations={
                    customer.paymentReconciliations as unknown as ReadonlyArray<PaymentReconciliationFragment>
                }
            />
        </div>
    );
}

function SavedCardsTable({ savedCards }: { savedCards: ReadonlyArray<CustomerSavedCardFragment> }) {
    return (
        <TableContainer component={Paper} style={{ marginBottom: "40px" }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width={"100px"} />
                        <TableCell width={"70px"} />
                        <TableCell width={timestampColumnWidth}>Registrert</TableCell>
                        <TableCell width={"200px"}>Status</TableCell>
                        <TableCell width={"100px"}>Kort</TableCell>
                        <TableCell width={"100px"}>Utløpsdato</TableCell>
                        <TableCell width={"130px"}>Land</TableCell>
                        <TableCell align="right" width={amountColumnWidth}>
                            <Typography variant={"caption"}>Beløp (kr)</Typography>
                            <br />
                            Trukket
                        </TableCell>
                        <TableCell align="right" width={amountColumnWidth}>
                            <Typography variant={"caption"}>Beløp (kr)</Typography>
                            <br />
                            Refundert
                        </TableCell>
                        <TableCell width={timestampColumnWidth}>Sist brukt</TableCell>
                        <TableCell width={"120px"}>
                            <Typography variant={"caption"}>Kortregistrering</Typography>
                            <br />
                            Terminal
                        </TableCell>
                        <TableCell>
                            <Typography variant={"caption"}>Kortregistrering</Typography>
                            <br />
                            Nets respons
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {savedCards.map((savedCard) => (
                        <SavedCardRow key={savedCard.id} savedCard={savedCard} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function formatCountryCode(issuerCountry: string | undefined) {
    if (!issuerCountry) {
        return null;
    } else if (issuerCountry === "NO") {
        return "Norge";
    } else {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <WarningIcon color={"error"} />
                {issuerCountry}
            </div>
        );
    }
}

function formatNetsTerminal(terminal: NetsTerminal) {
    switch (terminal) {
        case NetsTerminal.PiaSdk:
            return "Pia SDK";
        case NetsTerminal.WebView:
            return "WebView";
    }
}

function SavedCardRow({ savedCard }: { savedCard: CustomerSavedCardFragment }) {
    const [open, setOpen] = React.useState(
        savedCard.validity === CardValidity.Active || sumBy(savedCard.transactions, "amountCaptured") > 0,
    );
    const transactions = savedCard.transactions as unknown as ReadonlyArray<NetsTransactionFragment>;
    const transactionPreAuths =
        savedCard.transactionPreAuths as unknown as ReadonlyArray<NetsTransactionPreAuthFragment>;
    const netsTransactionObjects: ReadonlyArray<TransactionRef> = mergeTransactions(transactions, transactionPreAuths);
    const cardDetails = savedCard.cardDetails;
    const chipConfig = getCardValidityChip(savedCard.validity);

    // Calculate financial sums
    const totalCaptured = sumBy(transactions, "amountCaptured");
    const totalCredited = sumBy(transactions, (t) => sumBy(t.credits, "amountCredited"));

    return (
        <React.Fragment>
            <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{savedCard.id}</TableCell>
                <TableCell>
                    <TimestampText value={savedCard.createdTime} />
                </TableCell>
                <TableCell>
                    <Chip label={chipConfig.label} color={chipConfig.color} />
                </TableCell>
                <TableCell>{savedCard.displayName || "–"}</TableCell>
                <TableCell>{formatExpirationDate(cardDetails?.expirationDate)}</TableCell>
                <TableCell>{formatCountryCode(cardDetails?.issuerCountry)}</TableCell>
                <TableCell align="right">{totalCaptured > 0 ? formatMoney(totalCaptured) : null}</TableCell>
                <TableCell align="right">
                    {totalCaptured > 0 || totalCredited != 0 ? formatMoney(totalCredited) : null}
                </TableCell>
                <TableCell>
                    <TimestampText value={maxBy(transactions.map((t) => t.createdTime))} />
                </TableCell>
                <TableCell>{formatNetsTerminal(savedCard.terminal)}</TableCell>
                <TableCell>
                    <NetsResponseCode
                        operation={NetsOperation.Verify}
                        response={savedCard.verifyResponse as NetsProcessResponseFragment}
                    />
                </TableCell>
                <TableCell align={"right"}>
                    <NetsXmlButton savedCardId={savedCard.id} style={{ marginRight: "10px" }} />
                    <JsonDiagnosticButton name={"savedCard"} value={savedCard} />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <PropertyList>
                                <Property
                                    title={"Nets transaksjons-id"}
                                    value={savedCard.transactionId.transactionId}
                                />
                            </PropertyList>

                            <Typography variant="h6" gutterBottom>
                                Transaksjoner
                            </Typography>
                            <TransactionsTable rows={netsTransactionObjects} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

function TransactionsTable({ rows }: { rows: ReadonlyArray<TransactionRef> }) {
    return (
        <div style={{ width: "100%", overflowY: "hidden", overflowX: "auto" }}>
            <TableContainer sx={{ maxHeight: (window.innerHeight - 150) * 0.8 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <Typography variant={"caption"}>Kortbetaling</Typography>
                                <br />
                                Ordre-nummer
                            </TableCell>
                            <TableCell width={timestampColumnWidth}>
                                <Typography variant={"caption"}>Kortbetaling</Typography>
                                <br />
                                Startet
                            </TableCell>
                            <TableCell width={timestampColumnWidth}>
                                <Typography variant={"caption"}>Kortbetaling</Typography>
                                <br />
                                Fullført
                            </TableCell>
                            <TableCell>
                                <Typography variant={"caption"}>Kortbetaling</Typography>
                                <br />
                                Nets Respons
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant={"caption"}>Beløp (kr)</Typography>
                                <br />
                                Registrert
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant={"caption"}>Beløp (kr)</Typography>
                                <br />
                                Reservert
                            </TableCell>
                            <TableCell align="right" width={amountColumnWidth}>
                                <Typography variant={"caption"}>Beløp (kr)</Typography>
                                <br />
                                Trukket
                            </TableCell>
                            <TableCell align="right" width={amountColumnWidth}>
                                <Typography variant={"caption"}>Beløp (kr)</Typography>
                                <br />
                                Refundert
                            </TableCell>
                            <TableCell>
                                <Typography variant={"caption"}>Billett</Typography>
                                <br />
                                Billett-Id
                            </TableCell>
                            <TableCell width={timestampColumnWidth}>
                                <Typography variant={"caption"}>Billett</Typography>
                                <br />
                                Kjøpt
                            </TableCell>
                            <TableCell width={timestampColumnWidth}>
                                <Typography variant={"caption"}>Billett</Typography>
                                <br />
                                Prisfestet
                            </TableCell>
                            <TableCell>
                                <Typography variant={"caption"}>Billett</Typography>
                                <br />
                                Løpenummer
                            </TableCell>
                            <TableCell width={amountColumnWidth}>
                                <Typography variant={"caption"}>Billett</Typography>
                                <br />
                                Pris
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((transactionRef) => {
                            if (transactionRef.transaction) {
                                return (
                                    <TransactionRow
                                        key={transactionRef.netsTransactionId || transactionRef.transaction.id}
                                        transaction={transactionRef.transaction}
                                        preAuth={transactionRef.preAuth}
                                    />
                                );
                            } else {
                                return (
                                    <TransactionPreAuthRow
                                        key={transactionRef.netsTransactionId}
                                        preAuth={transactionRef.preAuth}
                                    />
                                );
                            }
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

function getTransactionDate(transaction: NetsTransactionFragment): ISODate | null {
    let dates = transaction.invoicesDelegated.map((invoice) => (invoice as InvoiceFragment).ticket.date);
    dates = uniq(dates);
    if (uniq(dates).length === 1) {
        return dates[0];
    } else {
        return null;
    }
}

function TransactionRow({
    transaction,
    preAuth,
}: {
    transaction: NetsTransactionFragment;
    preAuth: NetsTransactionPreAuthFragment | null;
}) {
    const invoicesDelegated = sortBy(
        transaction.invoicesDelegated as unknown as ReadonlyArray<InvoiceFragment>,
        (i) => i.ticket.id + "+" + i.invoiceNumber,
    );
    const ticketDate = getTransactionDate(transaction);

    let status: React.ReactElement;

    if (transaction.captureResponse) {
        status = (
            <NetsResponseCode
                operation={NetsOperation.Capture}
                response={transaction.captureResponse as NetsProcessResponseFragment}
            />
        );
    } else if (!transaction.processedSuccessfullyTime) {
        status = <Chip label={"Pending"} color={"warning"} />;
    } else {
        if (transaction.amount === transaction.amountCaptured) {
            status = <Chip label={"Betalt"} />;
        } else {
            status = <Chip label={"Error"} />;
        }
    }

    return (
        <React.Fragment>
            <TableRowBorderTop sx={{ "& > *": { borderBottom: "unset" } }}>
                <TableCell>
                    {/*{transaction.amountCaptured > 0 ? (*/}
                    {/*    <AttachMoney />*/}
                    {/*) : transaction.amountRegistered > 0 ? (*/}
                    {/*    <MoneyOff />*/}
                    {/*) : (*/}
                    {/*    <RemoveIcon />*/}
                    {/*)}*/}
                </TableCell>
                <TableCell>
                    <Link to={"/nets-transaction/" + transaction.id}>{transaction.id}</Link>
                </TableCell>
                <TableCell>
                    <TimestampText value={transaction.createdTime} />
                </TableCell>
                <TableCell>
                    {!transaction.processedSuccessfullyTime ? (
                        <Chip color={"error"} label={"Pending"} />
                    ) : (
                        <RelativeTimeText
                            includeSeconds={false}
                            value={transaction.processedSuccessfullyTime}
                            relativeTo={transaction.createdTime}
                        />
                    )}
                </TableCell>
                <TableCell>{status}</TableCell>
                <TableCell align="right">{formatMoney(transaction.amountRegistered)}</TableCell>
                <TableCell align="right">
                    {preAuth ? <TransactionPreAuthButton transaction={transaction} preAuth={preAuth} /> : null}
                </TableCell>
                <TableCell align="right">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                        {transaction.amount != transaction.amountCaptured ? (
                            <WarningIcon color={"error"} style={{ marginRight: "8px" }} />
                        ) : null}
                        {transaction.processedSuccessfullyTime || transaction.amountCaptured != 0
                            ? formatMoney(transaction.amountCaptured)
                            : null}
                    </div>
                </TableCell>
                <TableCell align="right">
                    {transaction.credits.length > 0 ? formatMoney(sumBy(transaction.credits, "amountCredited")) : null}
                </TableCell>
                <TableCell />
                <TableCell>{formatDateMed(ticketDate)}</TableCell>
                <TableCell />
                <TableCell align="right"></TableCell>
                <TableCell />
                <TableCell align={"right"}>
                    <JsonDiagnosticButton name={"transaction"} value={transaction} />
                </TableCell>
            </TableRowBorderTop>
            {invoicesDelegated.map((invoice) => (
                <InvoiceRow
                    key={(invoice as InvoiceFragment).invoiceNumber}
                    transaction={transaction}
                    invoice={invoice as InvoiceFragment}
                    showTicketDate={!ticketDate}
                />
            ))}
            <TableRow></TableRow>

            {transaction.credits.map((credit) => {
                return credit.invoices.map((i) => {
                    const invoice = i as InvoiceFragment;
                    return (
                        <InvoiceRow
                            transaction={transaction}
                            credit={credit}
                            invoice={invoice}
                            showTicketDate={!ticketDate}
                        />
                    );
                });
            })}
            <Divider />
        </React.Fragment>
    );
}
function TransactionPreAuthRow({ preAuth }: { preAuth: NetsTransactionPreAuthFragment }) {
    const date = parseTime(preAuth.createdTime);

    return (
        <TableRowBorderTop sx={{ "& > *": { borderBottom: "unset" } }}>
            <TableCell>{/*{preAuth.amountAuthorized != 0 ? <LockIcon /> : <LockOpen />}*/}</TableCell>
            <TableCell
                sx={{
                    maxWidth: 125,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
                title={preAuth.netsTransactionId}
            >
                {preAuth.netsTransactionId}
            </TableCell>
            <TableCell>
                <TimestampText value={preAuth.createdTime} />
            </TableCell>
            <TableCell>
                <RelativeTimeText
                    includeSeconds={false}
                    relativeTo={preAuth.createdTime}
                    value={preAuth.finalizedTime}
                />
            </TableCell>
            <TableCell>
                <NetsResponseCode
                    operation={NetsOperation.Auth}
                    response={preAuth.authResponse as NetsProcessResponseFragment}
                />
            </TableCell>
            <TableCell align="right">{formatMoney(preAuth.amount)}</TableCell>
            <TableCell align="right">
                {preAuth.amountAuthorized ? (
                    <Tooltip title={"Beløp reservert på kortet. Fjerns nattlig"}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <AccessTime color={"primary"} style={{ marginRight: "8px" }} />
                            {formatMoney(preAuth.amountAuthorized)}
                        </div>
                    </Tooltip>
                ) : null}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell>{date.toFormat(DateTimeFormat.DATE_MED)}</TableCell>
            <TableCell colSpan={3} />
            <TableCell padding={"none"} align={"right"}>
                <NetsXmlButton transactionId={preAuth.netsTransactionId} />
            </TableCell>
        </TableRowBorderTop>
    );
}

const TableRowBorderTop = styled(TableRow)(() => ({
    "& > *": {
        borderTop: "1px solid #000",
        borderBottom: "unset",
    },
}));
const TableRowFadedBorderTop = styled(TableRow)(() => ({
    "& > *": {
        borderTop: "1px solid #999",
        borderBottom: "unset",
    },
}));
const TableRowNoBorder = styled(TableRow)(() => ({
    "& > *": {
        borderTop: "unset",
        borderBottom: "unset",
    },
}));

function InvoiceRow({
    transaction,
    credit,
    invoice,
    showTicketDate,
}: {
    transaction: NetsTransactionFragment;
    credit?: NetsTransactionFragment["credits"][0];
    invoice: InvoiceFragment;
    showTicketDate: boolean;
}) {
    const removed =
        !credit &&
        transaction.invoices.find((i) => invoice.invoiceNumber == (i as InvoiceFragment).invoiceNumber) === undefined;
    const captureSuccess = transaction.amountCaptured == sumBy(transaction.invoicesDelegated, "totalAmount");
    const creditSuccess = credit && credit.amountCredited == -sumBy(credit.invoices, "totalAmount");

    const isLastTransactionInvoice =
        invoice === transaction.invoicesDelegated[transaction.invoicesDelegated.length - 1];
    const isFirstCreditInvoice = credit && credit == transaction.credits[0] && invoice === credit.invoices[0];

    const Row = isFirstCreditInvoice ? TableRowFadedBorderTop : TableRowNoBorder;

    return (
        <Row
            style={{
                textDecoration: removed ? "line-through" : undefined,
                borderBottom: isLastTransactionInvoice ? "1px solid #000" : undefined,
            }}
        >
            <TableCell colSpan={2} align={"right"} style={{ border: "unset" }}></TableCell>
            <TableCell>
                <TimestampText value={credit?.createdTime} />
            </TableCell>
            <TableCell>
                <RelativeTimeText
                    includeSeconds={false}
                    value={credit?.processedTime}
                    relativeTo={credit?.createdTime}
                />
            </TableCell>
            <TableCell>
                {credit ? (
                    <NetsResponseCode
                        operation={NetsOperation.Credit}
                        response={credit.creditResponse as NetsProcessResponseFragment}
                    />
                ) : null}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell align={"right"}>
                {credit ? formatAmountProcessed(creditSuccess ? -invoice.totalAmount : 0, -invoice.totalAmount) : null}
            </TableCell>
            <TableCell>
                <MaterialLink component={Link} to={"/ticket/" + invoice.ticket.id}>
                    {formatTicketId(invoice.ticket.id)}
                </MaterialLink>
            </TableCell>
            <TableCell>
                <TimestampText format={DateTimeFormat.DATETIME_MED} value={invoice.ticket.createdTime} />
            </TableCell>
            <TableCell>
                <RelativeTimeText value={invoice.createdTime} relativeTo={invoice.ticket.createdTime} />
            </TableCell>
            <TableCell>{invoice.invoiceNumber}</TableCell>
            <TableCell align={"right"}>{formatMoney(invoice.totalAmount)}</TableCell>
            <TableCell>
                {credit ? (
                    <Chip
                        icon={<WarningIcon />}
                        color={"error"}
                        label={"Kredittering"}
                        style={{ marginRight: "10px " }}
                    />
                ) : null}
                {invoice.complaint ? (
                    <MaterialLink component={Link} to={"/ticket/" + invoice.ticket.id + "/complaint"}>
                        Åpne klage
                    </MaterialLink>
                ) : null}
            </TableCell>
        </Row>
    );
}

function formatAmountProcessed(paid: number, total: number) {
    if (paid == total) return formatMoney(total);
    else return formatMoney(paid).replace(" kr", "") + " / " + formatMoney(total);
}
function PaymentReconciliationsTable(props: {
    paymentReconciliations: ReadonlyArray<PaymentReconciliationFragment>;
}): React.ReactElement | null {
    if (!isSuperUser()) return null;
    const list = sortBy(props.paymentReconciliations, "startedTime").toReversed();

    return (
        <div style={{ width: "fit-content" }}>
            <Typography variant="h6">
                Payment reconciliations
                <DeveloperModeIcon fontSize={"small"} />
            </Typography>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Kjørt</TableCell>
                            <TableCell>Trace</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {list.map((r) => {
                            return (
                                <TableRow key={r.id}>
                                    <TableCell>{r.id}</TableCell>
                                    <TableCell>
                                        <TimestampText value={r.startedTime} />
                                    </TableCell>
                                    <TableCell>
                                        <TraceButton trace={r.trace} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
